/**
 * Buchung anlegen (Screen laden/Buchung-anlegen): 5 Schritte auf einer Seite – Regal → Mieter → Zeitraum → Preis → Vertrag,
 * Zusammenfassung rechts. Kurzzeit/Saison/Rabatte/Aktionscodes/Warteliste sind V2.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { Button, Notice, Seitenzustand, StatusBadge, TextField, Total } from "../ui";
import { Card, FormGrid, PageHeader, Select, Split, Textarea } from "../ui/Layout";
import { ApiFehler } from "../api/client";
import { formatEuro, parseEuro } from "@shared/geld";
import { formatTag, tagVon } from "@shared/zeit";
import { endeVon } from "@shared/perioden";
import { useLaden } from "./api";
import { useRegalFrei, useRegale, type RegalZeile } from "./regale-api";
import { useMieterSuche } from "./mieter-api";
import { useBuchungAnlegen, type BuchungEingabe } from "./buchungen-api";
import { klassenPreisText } from "./Regale";
import { MieterFormular } from "./Mieter";
import { einheitText } from "./BuchungDetail";

export function BuchungAnlegen() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const laden = useLaden();
  const regale = useRegale();
  const anlegen = useBuchungAnlegen();
  const [regalId, setRegalId] = useState(params.get("regal") ?? "");
  const [mieter, setMieter] = useState<{ id: string; name: string; email?: string } | null>(null);
  const [suche, setSuche] = useState("");
  const [neuerMieter, setNeuerMieter] = useState(false);
  const [art, setArt] = useState<"fest" | "abo">("fest");
  const [einheit, setEinheit] = useState<"woche" | "monat">("monat");
  const [start, setStart] = useState(params.get("start") ?? tagVon());
  const [dauer, setDauer] = useState("3");
  const [miete, setMiete] = useState("");
  const [provision, setProvision] = useState("");
  const [kaution, setKaution] = useState("");
  const [zustimmung, setZustimmung] = useState<"link" | "vor_ort" | "spaeter">("link");
  const [notiz, setNotiz] = useState("");
  const [fehler, setFehler] = useState<string | null>(null);
  const [preisAngefasst, setPreisAngefasst] = useState(false);

  const e = laden.data?.einstellungen;
  const regal: RegalZeile | undefined = regale.data?.regale.find((r) => r.id === regalId);
  const treffer = useMieterSuche(suche);
  const mieterParam = params.get("mieter");
  useEffect(() => {
    if (mieterParam && !mieter) setSuche(" ");
  }, [mieterParam, mieter]);
  useEffect(() => {
    if (mieterParam && !mieter && treffer.data) {
      const t = treffer.data.treffer.find((x) => x.id === mieterParam);
      if (t) {
        setMieter({ id: t.id, name: t.name, email: t.email });
        setSuche("");
      }
    }
  }, [mieterParam, mieter, treffer.data]);

  // Einheit: nur mit Preis in der Klasse
  const kannWoche = regal?.klasse?.preisWocheCent !== null && regal?.klasse?.preisWocheCent !== undefined;
  const kannMonat = regal?.klasse?.preisMonatCent !== null && regal?.klasse?.preisMonatCent !== undefined;
  useEffect(() => {
    if (!regal?.klasse) return;
    if (einheit === "monat" && !kannMonat && kannWoche) setEinheit("woche");
    if (einheit === "woche" && !kannWoche && kannMonat) setEinheit("monat");
  }, [regal, einheit, kannMonat, kannWoche]);
  // Vorschläge aus Klasse und Einstellungen
  useEffect(() => {
    if (preisAngefasst || !e) return;
    const p = regal?.klasse ? (einheit === "woche" ? regal.klasse.preisWocheCent : regal.klasse.preisMonatCent) : null;
    setMiete(e.preise.modell === "provision" ? "0,00" : p !== null && p !== undefined ? (p / 100).toFixed(2).replace(".", ",") : "");
    setProvision(e.preise.modell === "miete" ? "0" : String(e.preise.provisionProzent));
    setKaution(e.preise.kautionCent ? (e.preise.kautionCent / 100).toFixed(2).replace(".", ",") : "0,00");
  }, [regal, einheit, e, preisAngefasst]);

  const dauerN = Math.max(1, parseInt(dauer, 10) || 1);
  const ende = art === "fest" && /^\d{4}-\d{2}-\d{2}$/.test(start) ? endeVon(start, einheit, dauerN) : null;
  const frei = useRegalFrei(regalId || null, /^\d{4}-\d{2}-\d{2}$/.test(start) ? start : null, ende);
  const mieteCent = parseEuro(miete) ?? 0;
  const provisionN = Math.max(0, Math.min(100, Number(provision.replace(",", ".")) || 0));
  const kautionCent = parseEuro(kaution) ?? 0;
  const summe = useMemo(() => (art === "fest" ? mieteCent * dauerN : mieteCent), [art, mieteCent, dauerN]);
  const bereit = !!regal && !!mieter && /^\d{4}-\d{2}-\d{2}$/.test(start) && frei.data?.frei !== false && (zustimmung !== "link" || !!mieter.email);

  async function buchen() {
    if (!regal || !mieter) return;
    setFehler(null);
    const d: BuchungEingabe = { regalId: regal.id, mieterId: mieter.id, art, einheit, start, dauer: art === "fest" ? dauerN : undefined, mieteCent, provisionProzent: provisionN, kautionCent, zustimmung, notiz };
    try {
      const r = await anlegen.mutateAsync(d);
      navigate(`/laden/buchungen/${r.id}`);
    } catch (err) {
      setFehler(err instanceof ApiFehler ? err.message : "Buchen hat nicht geklappt.");
    }
  }

  const schritt = !regal ? 1 : !mieter ? 2 : 3;
  const Nr = ({ n }: { n: number }) => <span className={["app-schritt-nr", n < schritt && "is-done", n === schritt && "is-on"].filter(Boolean).join(" ")}>{n}</span>;

  return (
    <>
      <PageHeader kicker={<Link to="/laden/belegung">Belegungskalender</Link>} title="Buchung anlegen" sub="Regal → Mieter → Zeitraum → Preis → Vertrag" />
      {fehler ? <Notice tone="error">{fehler}</Notice> : null}
      <Seitenzustand query={regale} lines={6}>
        {(rd) => (
          <Split
            main={
              <>
                <Card title={<span className="sc-row"><Nr n={1} /> Regal</span>}>
                  <FormGrid>
                    <Select label="Regal" value={regalId} onChange={(e) => { setRegalId(e.target.value); setPreisAngefasst(false); }}>
                      <option value="">– wählen –</option>
                      {rd.regale.filter((r) => r.aktiv).map((r) => (
                        <option key={r.id} value={r.id}>
                          {r.nummer}{r.lage ? ` · ${r.lage}` : ""}{r.klasse ? ` · ${r.klasse.name}` : ""} · {r.belegung === "frei" ? "frei" : r.belegung === "bald_frei" ? `frei ab ${r.aktuell?.ende ? formatTag(r.aktuell.ende) : ""}` : r.belegung === "reserviert" ? "reserviert" : `belegt${r.aktuell?.ende ? ` bis ${formatTag(r.aktuell.ende)}` : ""}`}
                        </option>
                      ))}
                    </Select>
                  </FormGrid>
                  {regal ? <p className="sc-muted" style={{ marginTop: "var(--space-3)" }}>Regal {regal.nummer}{regal.groesse ? ` · ${regal.groesse}` : ""}{regal.lage ? ` · ${regal.lage}` : ""} · {regal.klasse ? `${regal.klasse.name}: ${klassenPreisText(regal.klasse)}` : "keine Preisklasse – Miete frei eingeben"}</p> : null}
                </Card>

                <Card title={<span className="sc-row"><Nr n={2} /> Mieter</span>} actions={<Button variant="quiet" icon="plus" onClick={() => setNeuerMieter(true)}>Neuen Mieter anlegen</Button>}>
                  {mieter ? (
                    <div className="sc-row" style={{ justifyContent: "space-between" }}>
                      <span><span className="sc-strong">{mieter.name}</span>{mieter.email ? <span className="sc-muted"> · {mieter.email}</span> : <span className="sc-muted"> · ohne E-Mail</span>}</span>
                      <Button variant="quiet" icon="pencil" onClick={() => setMieter(null)}>Ändern</Button>
                    </div>
                  ) : (
                    <>
                      <TextField label="Mieter suchen" icon="search" value={suche} onChange={(e) => setSuche(e.target.value)} placeholder="Name, Nummer oder E-Mail" autoFocus={!!regal} />
                      {treffer.data?.treffer.length ? (
                        <div className="app-option-liste" style={{ marginTop: "var(--space-3)" }}>
                          {treffer.data.treffer.map((t) => (
                            <button key={t.id} type="button" className="sc-option" disabled={t.status !== "aktiv"} onClick={() => { setMieter({ id: t.id, name: t.name, email: t.email }); setSuche(""); }}>
                              <span className="sc-col" style={{ gap: 0 }}><span className="sc-strong">{t.name}</span><span className="sc-muted sc-small">Nr. {t.mieternummer}{t.email ? ` · ${t.email}` : " · ohne E-Mail"}{t.status !== "aktiv" ? ` · ${t.status}` : ""}</span></span>
                            </button>
                          ))}
                        </div>
                      ) : suche.trim() && treffer.data ? (
                        <p className="sc-muted" style={{ marginTop: "var(--space-3)" }}>Kein Mieter gefunden – oben „Neuen Mieter anlegen“.</p>
                      ) : null}
                    </>
                  )}
                </Card>

                <Card title={<span className="sc-row"><Nr n={3} /> Zeitraum</span>}>
                  <div className="app-option-liste">
                    <button type="button" className={["sc-option", art === "fest" && "is-on"].filter(Boolean).join(" ")} onClick={() => setArt("fest")}>
                      <span className="sc-radio" /><span className="sc-col" style={{ gap: 0 }}><span className="sc-strong">Fester Zeitraum</span><span className="sc-muted sc-small">Start und Dauer in Wochen oder Monaten, endet von selbst; verlängerbar</span></span>
                    </button>
                    {e?.preise.aboErlaubt ? (
                      <button type="button" className={["sc-option", art === "abo" && "is-on"].filter(Boolean).join(" ")} onClick={() => setArt("abo")}>
                        <span className="sc-radio" /><span className="sc-col" style={{ gap: 0 }}><span className="sc-strong">Miet-Abo</span><span className="sc-muted sc-small">Läuft weiter, bis gekündigt wird – Frist {e.preise.kuendigungsfristWochen} Wochen zum Monatsende</span></span>
                      </button>
                    ) : null}
                  </div>
                  <FormGrid>
                    <Select label="Abrechnung je" value={einheit} onChange={(e) => { setEinheit(e.target.value as "woche" | "monat"); setPreisAngefasst(false); }}>
                      {(!regal?.klasse || kannMonat) ? <option value="monat">Monat</option> : null}
                      {(!regal?.klasse || kannWoche) ? <option value="woche">Woche</option> : null}
                    </Select>
                    <TextField label="Start" type="date" value={start} onChange={(e) => setStart(e.target.value)} />
                    {art === "fest" ? <TextField label="Dauer" value={dauer} onChange={(e) => setDauer(e.target.value)} inputMode="numeric" suffix={einheitText(einheit, 2)} /> : null}
                    {art === "fest" ? <TextField label="Ende" value={ende ? formatTag(ende) : ""} readOnly hint="Monat = bis zum Vortag im Folgemonat" /> : null}
                  </FormGrid>
                  {regal && start ? (
                    frei.isPending ? <p className="sc-muted">Prüfe Verfügbarkeit …</p>
                    : frei.data?.frei ? <StatusBadge status="paid">Im Zeitraum frei</StatusBadge>
                    : frei.data ? <Notice tone="attention" title="Regal ist im Zeitraum belegt">{`Buchung ${frei.data.konflikt?.nummer} belegt es ${frei.data.konflikt ? formatTag(frei.data.konflikt.start) : ""}–${frei.data.konflikt?.ende ? formatTag(frei.data.konflikt.ende) : "offen"}.`}</Notice>
                    : null
                  ) : null}
                </Card>

                <Card title={<span className="sc-row"><Nr n={4} /> Preis und Kaution</span>}>
                  <FormGrid>
                    <TextField label={`Miete je ${einheitText(einheit)}`} value={miete} onChange={(e) => { setMiete(e.target.value); setPreisAngefasst(true); }} inputMode="decimal" suffix="€" hint={regal?.klasse ? `Vorschlag aus Klasse ${regal.klasse.name}` : "Frei eingeben"} />
                    <TextField label="Provision" value={provision} onChange={(e) => { setProvision(e.target.value); setPreisAngefasst(true); }} inputMode="decimal" suffix="%" hint="Vom Verkaufspreis nach Rabatt" />
                    <TextField label="Kaution" value={kaution} onChange={(e) => { setKaution(e.target.value); setPreisAngefasst(true); }} inputMode="decimal" suffix="€" hint="Einmalig bei Mietbeginn" />
                  </FormGrid>
                </Card>

                <Card title={<span className="sc-row"><Nr n={5} /> Vertrag</span>}>
                  <div className="app-option-liste">
                    <button type="button" className={["sc-option", zustimmung === "link" && "is-on"].filter(Boolean).join(" ")} onClick={() => setZustimmung("link")} disabled={!!mieter && !mieter.email}>
                      <span className="sc-radio" /><span className="sc-col" style={{ gap: 0 }}><span className="sc-strong">Zustimmung per Link</span><span className="sc-muted sc-small">{mieter && !mieter.email ? "Nicht möglich: Mieter hat keine E-Mail" : "Mieter bekommt den Vertrag per E-Mail, liest ihn im Portal und stimmt zu. Bis dahin ist das Regal reserviert."}</span></span>
                    </button>
                    <button type="button" className={["sc-option", zustimmung === "vor_ort" && "is-on"].filter(Boolean).join(" ")} onClick={() => setZustimmung("vor_ort")}>
                      <span className="sc-radio" /><span className="sc-col" style={{ gap: 0 }}><span className="sc-strong">Vor Ort zugestimmt (Papier)</span><span className="sc-muted sc-small">Mieter hat den ausgedruckten Vertrag unterschrieben; Papier wird abgeheftet, die Buchung gilt sofort.</span></span>
                    </button>
                    <button type="button" className={["sc-option", zustimmung === "spaeter" && "is-on"].filter(Boolean).join(" ")} onClick={() => setZustimmung("spaeter")}>
                      <span className="sc-radio" /><span className="sc-col" style={{ gap: 0 }}><span className="sc-strong">Nur reservieren</span><span className="sc-muted sc-small">Vertrag später senden oder vor Ort bestätigen.</span></span>
                    </button>
                  </div>
                  <Textarea label="Notiz (intern)" value={notiz} onChange={(e) => setNotiz(e.target.value)} rows={2} />
                </Card>
              </>
            }
            aside={
              <Card title="Zusammenfassung">
                <dl className="app-dl">
                  <dt>Regal</dt><dd>{regal ? `${regal.nummer}${regal.klasse ? ` · ${regal.klasse.name}` : ""}` : "–"}</dd>
                  <dt>Mieter</dt><dd>{mieter?.name ?? "–"}</dd>
                  <dt>Zeitraum</dt><dd>{art === "fest" ? `${formatTag(start)}–${ende ? formatTag(ende) : "–"}` : `ab ${formatTag(start)}, Abo`}</dd>
                  <dt>Miete</dt><dd>{art === "fest" ? `${dauerN} × ${formatEuro(mieteCent)}` : `${formatEuro(mieteCent)} je ${einheitText(einheit)}`}</dd>
                  <dt>Provision</dt><dd>{provisionN} % auf Verkäufe</dd>
                  <dt>Kaution</dt><dd>{formatEuro(kautionCent)}</dd>
                </dl>
                <Total amountCent={summe + kautionCent} label={art === "fest" ? "Miete gesamt + Kaution" : "Erste Periode + Kaution"} size="lg" caption="Wird mit dem Verkaufserlös verrechnet; Mietrechnungen ab Etappe 6" />
                <Button variant="primary" size="lg" block icon={zustimmung === "link" ? "mail" : "check"} disabled={!bereit} loading={anlegen.isPending} onClick={() => void buchen()}>
                  {zustimmung === "link" ? "Buchen und Vertrag senden" : zustimmung === "vor_ort" ? "Buchen (vor Ort zugestimmt)" : "Reservieren"}
                </Button>
                {!bereit && regal && mieter ? <p className="sc-muted sc-small">{zustimmung === "link" && !mieter.email ? "Für den Link braucht der Mieter eine E-Mail." : frei.data?.frei === false ? "Zeitraum ist belegt." : "Bitte alle Angaben prüfen."}</p> : null}
              </Card>
            }
          />
        )}
      </Seitenzustand>
      {neuerMieter ? <MieterFormular onClose={() => setNeuerMieter(false)} onAngelegt={(id, name, email) => { setMieter({ id, name, email }); setSuche(""); }} /> : null}
    </>
  );
}
